import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  ScrollView,
  Modal,
  Alert,
} from "react-native";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase.config";
import {
  createLock,
  deleteLock,
  updateLock,
} from "../db_calls_wrapper";
import { updateDocument } from "../db_calls";
import { getBookingsFromCache } from "../services/bookingsCache";
import AuthHeader from "../components/AuthHeader";
import { useSettings, useAuthStore, useLocks } from "../stores";

const auth = getAuth(app);

const Locks = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [shopInfo, setShopInfo] = useState({
    address: "",
    phone: "",
    email: "",
  });
  const [storeLogo, setStoreLogo] = useState("");
  const [storeName, setStoreName] = useState("");
  const locks = useLocks((state) => state.locks);
  const [combo, setCombo] = useState("");
  const [tagNumber, setTagNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [searchTag, setSearchTag] = useState("");
  const [filteredLocks, setFilteredLocks] = useState([]);
  const [highlightedLockId, setHighlightedLockId] = useState(null);
  const [comboChanges, setComboChanges] = useState({}); // Track changes: { lockId: { original: "", current: "" } }
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [lockToDelete, setLockToDelete] = useState(null);
  const flatListRef = useRef(null);
  const debounceTimers = useRef({});

  // Track combo changes for save button enable/disable
  const handleComboChange = (lockId, newCombo, originalCombo) => {
    // Track the change
    setComboChanges((prev) => ({
      ...prev,
      [lockId]: {
        original: originalCombo || "",
        current: newCombo || "",
      },
    }));
  };

  // Save combo changes to DB
  const handleSaveCombo = async (lockId) => {
    const change = comboChanges[lockId];
    if (!change || !change.current || !change.current.trim()) {
      alert("Please enter a combination");
      return;
    }

    try {
      await updateLock(lockId, {
        combination: change.current.trim(),
        combo: change.current.trim(),
        updatedAt: new Date(),
      });
      console.log(`✅ Updated combo for lock ${lockId}: ${change.current}`);

      // Clear the change tracking for this lock
      setComboChanges((prev) => {
        const updated = { ...prev };
        delete updated[lockId];
        return updated;
      });
    } catch (error) {
      console.error("❌ Failed to update combo:", error);
      alert("Failed to save combination: " + error.message);
    }
  };

  // Handle delete lock
  const handleDeleteLock = async () => {
    if (!lockToDelete) return;

    try {
      await deleteLock(lockToDelete.id);
      console.log(`✅ Deleted lock ${lockToDelete.id}`);

      // Clear change tracking if it exists
      setComboChanges((prev) => {
        const updated = { ...prev };
        delete updated[lockToDelete.id];
        return updated;
      });

      setDeleteModalVisible(false);
      setLockToDelete(null);
    } catch (error) {
      console.error("❌ Failed to delete lock:", error);
      alert("Failed to delete lock: " + error.message);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (lock) => {
    setLockToDelete(lock);
    setDeleteModalVisible(true);
  };

  useEffect(() => {
    // Redirect if not authenticated
    if (!isAuthenticated) {
        navigate("/login");
    } else {
        setLoading(false);
    }
  }, [isAuthenticated, navigate]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load settings
  // Get settings from Zustand store
  const settings = useSettings((state) => state.settings);
  
  // Update state when settings change
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      const address = settings.startingAddress?.address || "";
      const phone = settings.storePhone?.phone || "";
      const email = settings.storeEmail?.email || "";
      const logo = settings.storeLogo?.url || "";
      const storeDisplayName = settings.storeName?.name || "";
      
      setShopInfo({ address, phone, email });
      setStoreLogo(logo);
      setStoreName(storeDisplayName);
    }
  }, [settings]);

  // Locks are already initialized globally in App.js
  // Just read from Zustand store - no need to initialize again
  useEffect(() => {
    console.log('🔒 Locks page loaded. Locks in store:', locks.length);
    if (locks.length > 0) {
      console.log('🔒 Sample lock:', locks[0]);
    }
  }, [locks]);

  // Filter locks by tag number search - reacts to Zustand store changes
  useEffect(() => {
    console.log('🔒 Filtering locks. Total:', locks.length, 'Search:', searchTag);
    if (!searchTag.trim()) {
      setFilteredLocks(locks);
    } else {
      const filtered = locks.filter((lock) => {
        const tagStr = (lock.tagNumber?.toString() || lock.name?.toString() || '').toLowerCase();
        return tagStr.includes(searchTag.toLowerCase());
      });
      setFilteredLocks(filtered);
      console.log('🔒 Filtered locks:', filtered.length);
    }
  }, [searchTag, locks]);

  // Helper function to find which booking a lock is assigned to
  const findAssignedBooking = (lockTagNumber) => {
    const bookings = getBookingsFromCache();
    const assignedBooking = bookings.find(
      (booking) =>
        booking.assignedLocks && booking.assignedLocks.includes(lockTagNumber)
    );
    return assignedBooking;
  };

  const handleAddLock = async () => {
    // Validate inputs
    if (!combo.trim()) {
      alert("Please enter a combination");
      return;
    }
    if (!tagNumber.trim()) {
      alert("Please enter a tag number");
      return;
    }

    // Validate tag number is an integer
    const tagNum = parseInt(tagNumber.trim());
    if (isNaN(tagNum)) {
      alert("Tag number must be a valid number");
      return;
    }

    setSaving(true);

    try {
      const lockId = await createLock({
        combo: combo.trim(),
        tagNumber: tagNum,
        name: tagNum.toString(), // Save exact tag number as name
      });

      console.log("✅ Lock created:", lockId);

      // Clear form
      setCombo("");
      setTagNumber("");
    } catch (error) {
      console.error("❌ Error creating lock:", error);
      alert("Failed to create lock: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Loading...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Lock Management"
        subtitle={storeName ? `by ${storeName}` : ""}
        isDesktop={isDesktop}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      
      {/* Floating Back Button */}
      <TouchableOpacity dataSet={{ hover: "lift" }}
        style={[
          styles.floatingBackButton,
          isDesktop && styles.floatingBackButtonDesktop
        ]}
        onPress={() => navigate(-1)}
      >
        <Text style={styles.floatingBackButtonText}>←</Text>
      </TouchableOpacity>

      <View style={isDesktop ? styles.desktopWrapper : null}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
      {/* Locks List */}
      <View style={styles.listContainer}>
        {/* Search Bar - Centered */}
        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder="🔍 Search by tag #..."
            placeholderTextColor="#8A9794"
            value={searchTag}
            onChangeText={setSearchTag}
            keyboardType="number-pad"
          />
        </View>

        {/* Add Lock Form */}
        <View style={styles.inputRow}>
          <TextInput
            style={[styles.input, styles.comboInput]}
            placeholder="Combo"
            placeholderTextColor="#AEB8B6"
            value={combo}
            onChangeText={setCombo}
            keyboardType="number-pad"
            editable={!saving}
          />
          <TextInput
            style={[styles.input, styles.tagInput]}
            placeholder="Tag #"
            placeholderTextColor="#AEB8B6"
            value={tagNumber}
            onChangeText={setTagNumber}
            keyboardType="number-pad"
            editable={!saving}
          />
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              styles.addButton,
              (saving || !combo.trim() || !tagNumber.trim()) &&
                styles.addButtonDisabled,
            ]}
            onPress={handleAddLock}
            disabled={saving || !combo.trim() || !tagNumber.trim()}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text style={styles.addButtonText}>+</Text>
            )}
          </TouchableOpacity>
        </View>

        {filteredLocks.length === 0 && locks.length > 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyIcon}>🔍</Text>
            <Text style={styles.emptyText}>No locks found</Text>
            <Text style={styles.emptySubtext}>Try a different tag number</Text>
          </View>
        ) : locks.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyIcon}>🔒</Text>
            <Text style={styles.emptyText}>No locks yet</Text>
            <Text style={styles.emptySubtext}>
              Add your first lock using the form below
            </Text>
          </View>
        ) : (
          <FlatList
            ref={flatListRef}
            data={filteredLocks}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => {
              const isHighlighted = highlightedLockId === item.id;

              // Check if lock is assigned to any booking
              const assignedBooking = findAssignedBooking(item.tagNumber);
              const isAssigned = !!assignedBooking;
              const customerName = assignedBooking
                ? assignedBooking.fullName ||
                  `${assignedBooking.firstName || ""} ${
                    assignedBooking.lastName || ""
                  }`.trim() ||
                  "Unknown Customer"
                : null;

              return (
                <View
                  style={[
                    styles.lockItem,
                    isHighlighted && styles.lockItemHighlighted,
                  ]}
                >
                  <View style={styles.lockInfo}>
                    {/* Tag and Combo Row */}
                    <View style={styles.lockHeader}>
                      <View style={styles.lockFieldContainer}>
                        <Text style={styles.lockFieldLabel}>🏷️ #</Text>
                        <Text style={styles.lockFieldValue}>
                          {item.tagNumber || item.name}
                        </Text>
                      </View>
                      <View style={styles.lockFieldContainer}>
                        <Text style={styles.lockFieldLabel}>🔢</Text>
                        <TextInput
                          style={styles.lockComboInput}
                          value={
                            comboChanges[item.id]?.current !== undefined
                              ? comboChanges[item.id].current
                              : item.combination || item.combo || ""
                          }
                          onChangeText={(text) =>
                            handleComboChange(
                              item.id,
                              text,
                              item.combination || item.combo || ""
                            )
                          }
                          keyboardType="number-pad"
                          maxLength={7}
                          placeholder="Combo"
                          placeholderTextColor="#AEB8B6"
                        />
                      </View>
                      {/* Save and Delete Buttons */}
                      <View style={styles.lockActions}>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[
                            styles.iconButton,
                            (!comboChanges[item.id] ||
                              comboChanges[item.id].current ===
                                comboChanges[item.id].original) &&
                              styles.iconButtonDisabled,
                          ]}
                          onPress={() => handleSaveCombo(item.id)}
                          disabled={
                            !comboChanges[item.id] ||
                            comboChanges[item.id].current ===
                              comboChanges[item.id].original
                          }
                        >
                          <Text style={styles.iconButtonText}>💾</Text>
                        </TouchableOpacity>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.iconButton}
                          onPress={() => openDeleteModal(item)}
                        >
                          <Text style={styles.iconButtonText}>🗑️</Text>
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Assigned Status and Customer Name Row */}
                    {isAssigned && (
                      <View style={styles.assignedRow}>
                        <View style={styles.assignedBadge}>
                          <Text style={styles.assignedBadgeText}>ASSIGNED</Text>
                        </View>
                        <View style={styles.assignedRightGroup}>
                          {customerName && (
                            <Text style={styles.assignedCustomerName}>
                              {customerName}
                            </Text>
                          )}
                          <TouchableOpacity
                            onPress={async () => {
                              if (
                                window.confirm(
                                  `Remove lock #${item.tagNumber} from ${customerName}?`
                                )
                              ) {
                                try {
                                  // Remove this lock from the rental's assignedLocks array
                                  const updatedLocks = (
                                    assignedBooking.assignedLocks || []
                                  ).filter(
                                    (lockTag) => lockTag !== item.tagNumber
                                  );
                                  await updateDocument(
                                    "bookings",
                                    assignedBooking.id,
                                    {
                                      assignedLocks: updatedLocks,
                                    }
                                  );
                                  console.log(
                                    `✅ Removed lock #${item.tagNumber} from rental`
                                  );
                                } catch (error) {
                                  console.error(
                                    "❌ Failed to remove lock:",
                                    error
                                  );
                                  alert("Failed to remove lock assignment");
                                }
                              }
                            }}
                            style={styles.removeLockButton}
                          >
                            <Text style={styles.removeLockButtonText}>✕</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              );
            }}
            contentContainerStyle={styles.flatListContent}
            style={styles.flatList}
            onScrollToIndexFailed={(info) => {
              console.warn("Scroll to index failed:", info);
            }}
          />
        )}
          </View>
        </ScrollView>
      </View>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={deleteModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setDeleteModalVisible(false);
          setLockToDelete(null);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Delete Lock?</Text>
            <Text style={styles.modalText}>
              Are you sure you want to delete lock #{lockToDelete?.tagNumber || lockToDelete?.name}?
            </Text>
            <Text style={styles.modalWarning}>
              This action cannot be undone.
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.modalButton, styles.modalButtonCancel]}
                onPress={() => {
                  setDeleteModalVisible(false);
                  setLockToDelete(null);
                }}
              >
                <Text style={styles.modalButtonCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.modalButton, styles.modalButtonDelete]}
                onPress={handleDeleteLock}
              >
                <Text style={styles.modalButtonDeleteText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  scrollView: {
    flex: 1,
  },
  floatingBackButton: {
    position: "absolute",
    top: 80,
    left: 16,
    zIndex: 1000,
    backgroundColor: "rgba(0, 122, 255, 0.5)",
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  floatingBackButtonDesktop: {
    left: "calc(50% - 400px + 16px)",
  },
  floatingBackButtonText: {
    fontSize: 24,
    color: "#FFFFFF",
    fontWeight: "bold",
  },
  listContainer: {
    flex: 1,
    padding: 16,
  },
  listTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
  },
  searchContainer: {
    alignItems: "center",
    marginBottom: 12,
  },
  searchInput: {
    width: 300,
    height: 40,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 14,
    backgroundColor: "#FFFFFF",
    outlineStyle: "none",
    textAlign: "center",
  },
  flatList: {
    flex: 1,
  },
  flatListContent: {
    paddingBottom: 100,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 40,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 15,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
  },
  lockItem: {
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    padding: 10,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  lockItemHighlighted: {
    backgroundColor: "#FDF3E3",
    borderColor: "#B4761B",
    borderWidth: 2,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
  },
  lockInfo: {
    flex: 1,
  },
  lockHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  lockFieldContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  lockFieldLabel: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  lockFieldValue: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#2E3A38",
    minWidth: 60,
  },
  lockTag: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  lockCombo: {
    fontSize: 16,
    fontWeight: "600",
    color: "#0E7C6B",
    letterSpacing: 1,
  },
  lockComboInput: {
    fontSize: 16,
    fontWeight: "600",
    color: "#0E7C6B",
    letterSpacing: 1,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 5,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "#F4F6F6",
    minWidth: 80,
    maxWidth: 100,
    outlineStyle: "none",
  },
  assignedRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  assignedBadge: {
    backgroundColor: "#B4761B",
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  assignedBadgeText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "bold",
  },
  assignedRightGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  assignedCustomerName: {
    fontSize: 14,
    color: "#5C6B68",
    fontWeight: "500",
  },
  removeLockButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  removeLockButtonText: {
    fontSize: 20,
    color: "#C4453D",
    fontWeight: "bold",
  },
  lockAssignedTo: {
    fontSize: 13,
    color: "#5C6B68",
    marginTop: 2,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    maxWidth: "100%",
    marginBottom: 20,
  },
  input: {
    height: 45,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 14,
    backgroundColor: "#F4F6F6",
    outlineStyle: "none",
    minWidth: 0,
  },
  comboInput: {
    flex: 1,
    minWidth: 80,
  },
  tagInput: {
    flex: 1,
    minWidth: 80,
  },
  addButton: {
    backgroundColor: "#0E7C6B",
    width: 45,
    height: 45,
    borderRadius: 14.5,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
    flexShrink: 0,
  },
  addButtonDisabled: {
    backgroundColor: "#AEB8B6",
  },
  addButtonText: {
    color: "#FFFFFF",
    fontSize: 36,
    fontWeight: "300",
    lineHeight: 45,
    textAlign: "center",
  },
  title: {
    fontSize: 18,
    color: "#5C6B68",
  },
  lockActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  iconButton: {
    padding: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  iconButtonDisabled: {
    opacity: 0.3,
  },
  iconButtonText: {
    fontSize: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "100%",
    maxWidth: 400,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 5,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
  },
  modalText: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 8,
    lineHeight: 22,
  },
  modalWarning: {
    fontSize: 14,
    color: "#C4453D",
    fontWeight: "600",
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  modalButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    minWidth: 80,
    alignItems: "center",
  },
  modalButtonCancel: {
    backgroundColor: "#EDF1F0",
  },
  modalButtonCancelText: {
    color: "#5C6B68",
    fontSize: 16,
    fontWeight: "600",
  },
  modalButtonDelete: {
    backgroundColor: "#C4453D",
  },
  modalButtonDeleteText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default Locks;
